<template>
    <div id="favoriteTeamsTable">
        <div v-if="loading" class="text-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>

        <div v-else-if="teams.length > 0">
            <div class="d-flex">
                <h3>
                    Favorite Teams
                </h3>
                <div v-if="perPageOptions.length > 0" class="ml-auto select-blue mb-2">
                    <select
                        v-model="selectedPerPage"
                        @change="changePerPage"
                        class="form-control"
                    >
                        <option v-for="option in perPageOptions" :key="option" :value="option">
                            {{ option }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="row">
                <div
                    v-for="team in teams"
                    :key="team.id"
                    class="col-3 d-flex align-items-stretch"
                >
                    <team-card
                        :team="team"
                        :enable-toggle-favorite="enableToggleFavorite"
                        @favorite-toggled="handleFavoriteToggled"
                    />
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <nav>
                        <ul class="pagination pagination-sm">
                            <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                <a class="page-link" @click.prevent="goToPage(pagination.current_page - 1)" href="#">Previous</a>
                            </li>
                            <li
                                v-for="page in visiblePages"
                                :key="page"
                                class="page-item"
                                :class="{ active: page === pagination.current_page }"
                            >
                                <a class="page-link" @click.prevent="goToPage(page)" href="#">{{ page }}</a>
                            </li>
                            <li class="page-item"
                                :class="{ disabled: pagination.current_page === pagination.last_page }">
                                <a class="page-link" @click.prevent="goToPage(pagination.current_page + 1)" href="#">Next</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>

        <div v-else-if="!loading" class="no-data">
            No favorite teams
        </div>
    </div>
</template>

<script>
import TeamCard from '../shared/cards/TeamCard.vue';

export default {
    name: 'FavoriteTeamsTable',

    components: {
        TeamCard,
    },

    props: {
        options: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            teams: [],
            loading: true,
            selectedPerPage: 12,
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 12,
                total: 0,
                from: 0,
                to: 0,
                prev_page_url: null,
                next_page_url: null,
            },
        };
    },

    computed: {
        perPage() {
            return parseInt(this.selectedPerPage) || 12;
        },

        perPageOptions() {
            return this.options.per_page_options || [12, 24, 48];
        },

        enableToggleFavorite() {
            return this.options.enable_toggle_favorite || false;
        },

        visiblePages() {
            const current = this.pagination.current_page;
            const last = this.pagination.last_page;
            const pages = [];

            // Show up to 5 page numbers around current page
            const start = Math.max(1, current - 2);
            const end = Math.min(last, current + 2);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        },
    },

    mounted() {
        // Set initial per_page from options
        this.selectedPerPage = this.options.per_page || 12;
        this.loadFavoriteTeams();
    },

    methods: {
        async loadFavoriteTeams(page = 1) {
            try {
                this.loading = true;

                const response = await axios.get('/api/favorites/teams', {
                    params: {
                        per_page: this.perPage,
                        page: page,
                    },
                });

                this.teams = response.data.data;
                this.pagination = {
                    current_page: response.data.current_page,
                    last_page: response.data.last_page,
                    per_page: response.data.per_page,
                    total: response.data.total,
                    from: response.data.from,
                    to: response.data.to,
                    prev_page_url: response.data.prev_page_url,
                    next_page_url: response.data.next_page_url,
                };
            } catch (error) {
                console.error('Failed to load favorite teams:', error);
                this.teams = [];
            } finally {
                this.loading = false;
            }
        },

        changePerPage() {
            this.loadFavoriteTeams(1); // Reset to first page when changing per page
        },

        goToPage(page) {
            if (page >= 1 && page <= this.pagination.last_page && page !== this.pagination.current_page) {
                this.loadFavoriteTeams(page);
            }
        },

        handleFavoriteToggled(teamId) {
            // Remove the team from the list if it was unfavorited
            this.teams = this.teams.filter(team => team.id !== teamId);

            // If we're on the last page and it becomes empty, go to previous page
            if (this.teams.length === 0 && this.pagination.current_page > 1) {
                this.goToPage(this.pagination.current_page - 1);
            } else if (this.teams.length === 0) {
                // If it's the first page and empty, reload to update pagination
                this.loadFavoriteTeams(1);
            }
        },
    },
};
</script>

<style scoped>
.no-data {
    text-align: center;
    padding: 2rem;
    color: #6c757d;
}

.select-blue select {
    border-color: #007bff;
}

.pagination-sm .page-link {
    cursor: pointer;
}

.pagination-sm .page-item.disabled .page-link {
    cursor: not-allowed;
}
</style>